import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Bell, SignOut, User as UserIcon, House, Compass, Plus, Sun, Moon, Translate, Crown, Trophy } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { useTheme } from "@/context/ThemeContext";
import api from "@/lib/api";

export default function AppHeader() {
  const { user, logout } = useAuth();
  const { t, lang, setLang } = useI18n();
  const { theme, toggle } = useTheme();
  const [isPro, setIsPro] = useState(false);
  useEffect(() => {
    if (!user) return;
    api.get("/pro/me").then((r) => setIsPro(!!r.data?.is_pro)).catch(() => {});
  }, [user]);
  return (
    <header className="tf-glass sticky top-0 z-40">
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
        <Link to="/dashboard" data-testid="nav-logo" className="flex items-center gap-2">
          <div className="w-7 h-7 bg-[#007AFF] flex items-center justify-center">
            <span className="font-display text-white text-sm">TF</span>
          </div>
          <span className="font-display text-xl tracking-tight">TEAMFIT</span>
        </Link>
        <nav className="hidden md:flex items-center gap-1">
          <NavLink to="/dashboard" icon={<House weight="duotone" size={18} />} label={t("nav.home")} testId="nav-home" />
          <NavLink to="/explore" icon={<Compass weight="duotone" size={18} />} label={t("nav.explore")} testId="nav-explore" />
          {isPro && (
            <NavLink to="/leaderboard" icon={<Trophy weight="duotone" size={18} />} label={lang === "da" ? "Rangliste" : "Leaderboard"} testId="nav-leaderboard" />
          )}
          <NavLink to="/create" icon={<Plus weight="bold" size={18} />} label={t("nav.create")} testId="nav-create" />
          <Link to="/pro" data-testid="nav-pro" className={`flex items-center gap-2 px-3 py-2 text-sm rounded-sm transition-colors ${isPro ? "text-[#FFCC00] hover:bg-[#FFCC00]/10" : "text-zinc-200 hover:bg-white/5 hover:text-white"}`}>
            <Crown weight={isPro ? "fill" : "duotone"} size={18} />
            <span>{isPro ? "Pro" : (lang === "da" ? "Bliv Pro" : "Go Pro")}</span>
          </Link>
        </nav>
        <div className="flex items-center gap-1">
          <button
            data-testid="lang-toggle"
            onClick={() => setLang(lang === "da" ? "en" : "da")}
            className="px-2 py-2 hover:bg-white/5 rounded-sm flex items-center gap-1 text-xs uppercase tracking-widest font-bold"
            aria-label="Change language"
          >
            <Translate weight="duotone" size={16} />
            <span>{lang.toUpperCase()}</span>
          </button>
          <button
            data-testid="theme-toggle"
            onClick={toggle}
            className="p-2 hover:bg-white/5 rounded-sm transition-colors"
            aria-label="Toggle theme"
          >
            {theme === "dark" ? <Sun weight="duotone" size={18} /> : <Moon weight="duotone" size={18} />}
          </button>
          <Link to="/notifications" data-testid="nav-notifications" className="p-2 hover:bg-white/5 rounded-sm transition-colors" aria-label="Notifications">
            <Bell weight="duotone" size={20} />
          </Link>
          <Link to="/profile" data-testid="nav-profile" className="p-2 hover:bg-white/5 rounded-sm transition-colors flex items-center gap-2">
            {user?.picture ? (
              <img src={user.picture.startsWith("http") ? user.picture : `${process.env.REACT_APP_BACKEND_URL}${user.picture}`} alt="" className="w-7 h-7 object-cover" />
            ) : (
              <div className="w-7 h-7 bg-[#1A1A1A] border border-[#262626] flex items-center justify-center">
                <UserIcon size={14} weight="duotone" />
              </div>
            )}
          </Link>
          <button data-testid="nav-logout" onClick={logout} className="p-2 hover:bg-white/5 rounded-sm transition-colors" aria-label={t("common.logout")}>
            <SignOut weight="duotone" size={20} />
          </button>
        </div>
      </div>
    </header>
  );
}

function NavLink({ to, icon, label, testId }) {
  return (
    <Link
      to={to}
      data-testid={testId}
      className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-white/5 rounded-sm text-zinc-200 hover:text-white transition-colors"
    >
      {icon}
      <span>{label}</span>
    </Link>
  );
}
